'use client';

import * as Dialog from '@radix-ui/react-dialog';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { StickyNote, X } from 'lucide-react';
import { useCreateNote, useUpdateNote } from '@/hooks/useNotes';
import { useStickyNotePrefs } from '@/hooks/useStickyNotePrefs';
import { Tooltip } from '@/components/ui/Tooltip';
import { toast } from '@/lib/toast';

// Quick-note shortcut. Two visual variants, both opening the same
// quick-create dialog. The user picks visibility + variant in
// /settings (Notes & Timer tab).
//
//   floating — a round glass button pinned bottom-right, amber icon.
//   stack    — three dark glass sheets peeking from the right edge,
//              the top one carrying an amber spine.

export function StickyNoteShortcut() {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const { prefs } = useStickyNotePrefs();

  // Hide on the notes page itself — the sidebar's "New note" is the
  // right affordance there.
  if (pathname?.startsWith('/notes')) return null;
  if (!prefs.enabled) return null;

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Tooltip label="Quick note">
        <Dialog.Trigger asChild>
          {prefs.variant === 'stack' ? (
            // Stack variant. Decorative sheets are pointer-events-none
            // so the whole surface routes clicks to the button (Radix
            // Slot needs the actual <button> to receive its onClick).
            <button
              type="button"
              aria-label="Quick note"
              className="group fixed right-2 top-1/2 z-30 -translate-y-1/2 bg-transparent p-0 transition duration-base ease-out-quint hover:-translate-x-1"
              style={{ width: 54, height: 70 }}
            >
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 rounded-lg border border-hairline bg-surface-sunken"
                style={{ transform: 'translate(6px, 8px) rotate(4deg)' }}
              />
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 rounded-lg border border-hairline bg-surface-muted"
                style={{ transform: 'translate(3px, 4px) rotate(-3deg)' }}
              />
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden rounded-lg border border-border bg-surface text-accent shadow-lg transition duration-fast ease-out-quint group-hover:-translate-y-0.5 group-hover:rotate-1 group-hover:shadow-glow"
                style={{ transform: 'rotate(1deg)' }}
              >
                <span aria-hidden className="absolute inset-y-0 left-0 w-[3px] bg-accent" />
                <StickyNote className="h-5 w-5" strokeWidth={1.7} />
              </span>
            </button>
          ) : (
            <button
              type="button"
              aria-label="Quick note"
              className="glass fixed bottom-20 right-5 z-30 flex h-12 w-12 items-center justify-center rounded-full border border-border text-accent transition duration-fast ease-out-quint hover:-translate-y-0.5 hover:border-accent/60 hover:shadow-glow lg:bottom-6 lg:right-6"
            >
              <StickyNote className="h-5 w-5" strokeWidth={1.7} />
            </button>
          )}
        </Dialog.Trigger>
      </Tooltip>
      <StickyNoteDialog onSaved={() => setOpen(false)} />
    </Dialog.Root>
  );
}

function StickyNoteDialog({ onSaved }: { onSaved: () => void }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const titleRef = useRef<HTMLInputElement>(null);
  const create = useCreateNote();
  const update = useUpdateNote();

  useEffect(() => {
    const t = window.setTimeout(() => titleRef.current?.focus(), 60);
    return () => window.clearTimeout(t);
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (saving) return;
    const trimmedTitle = title.trim();
    const trimmedBody = body.trim();
    if (!trimmedTitle && !trimmedBody) {
      onSaved();
      return;
    }
    setSaving(true);
    try {
      const note = await create.mutateAsync();
      await update.mutateAsync({
        id: note.id,
        title: trimmedTitle || 'Untitled',
        body: trimmedBody,
      });
      toast.success('Saved to Notes');
      onSaved();
    } catch {
      toast.error("Couldn't save that note");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog.Portal>
      <Dialog.Overlay className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=open]:fade-in" />
      <Dialog.Content className="fixed left-1/2 top-[40%] z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-xl border border-hairline bg-surface p-6 shadow-xl focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=open]:fade-in data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">
        <span aria-hidden className="absolute inset-x-0 top-0 h-[3px] bg-accent" />
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <p className="eyebrow" style={{ color: 'hsl(var(--accent))' }}>
              Quick note
            </p>
            <Dialog.Title className="font-display text-xl font-bold leading-tight tracking-tight">
              Jot it down.
            </Dialog.Title>
            <Dialog.Description className="pt-1 text-[13px] text-muted-foreground">
              Lands in your Notes page when you save.
            </Dialog.Description>
          </div>
          <Tooltip label="Close">
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Close"
                className="-m-1 rounded-lg p-1.5 text-muted-foreground transition duration-fast ease-out-quint hover:bg-surface-muted hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </Tooltip>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <input
            ref={titleRef}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            placeholder="Title"
            aria-label="Note title"
            className="w-full rounded-lg border border-border bg-surface-sunken/60 px-3 py-2 text-[15px] outline-none transition duration-fast ease-out-quint placeholder:text-subtle-foreground focus:border-accent"
          />
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={10_000}
            rows={6}
            placeholder="What's on your mind?"
            aria-label="Note body"
            className="w-full resize-y rounded-lg border border-border bg-surface-sunken/60 px-3 py-2 text-[14px] leading-relaxed outline-none transition duration-fast ease-out-quint placeholder:text-subtle-foreground focus:border-accent"
          />
          <div className="flex items-center justify-end gap-2">
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded-full px-3 py-2 text-sm font-medium text-muted-foreground transition duration-fast ease-out-quint hover:bg-surface-muted hover:text-foreground"
              >
                Cancel
              </button>
            </Dialog.Close>
            <button
              type="submit"
              disabled={saving}
              className="rounded-full bg-accent px-4 py-2 text-sm font-semibold text-primary-foreground transition duration-fast ease-out-quint hover:shadow-glow hover:brightness-105 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save note'}
            </button>
          </div>
        </form>
      </Dialog.Content>
    </Dialog.Portal>
  );
}
